import { useFieldArray, useForm, useWatch } from "react-hook-form";
import useLabels from "../../hooks/useLabels";
import {
  DatePicker,
  Input,
  Label,
  MultipleSelect,
  Select,
  Submit,
  TextArea,
} from "../FormComponents";
import { format } from "date-fns";
import { useNavigate, useParams } from "react-router";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import { useDispatch } from "react-redux";
import { newOperation, updateOperation } from "../../services/utils";
import { add, empty, updateOp } from "../../context/operations/operationsSlice";
import { useEffect, useRef, useState } from "react";
import SubstanceList from "../SubstanceList";
import { ConfirmationModal, OperationRecap, TankIcon } from "../Generic";
import {
  ALPHA_NUM_SPACE_DASH_UNDERSCORE_REGEX,
  INT_REGEX,
} from "../../constants/regex";
import { toast } from "react-toastify";
import TableCell from "../TableEdit/TableCell";
import { createColumnHelper } from "@tanstack/react-table";
import EditCell from "../TableEdit/EditCell";
import TableEdit from "../TableEdit";
import { DestLotDetail } from "../OperationsDetail/Details";
import { toastAxios } from "../../services/toastAxios";
import { aggregateDrugsById } from "../../services/common";

const AutomaticForm = ({
  type,
  operation,
  operations,
  protocols,
  allSubstances,
  activeWinery,
  tanks,
  users,
  configs,
  lots,
  isDuplicate = false,
}) => {
  const { id } = useParams();
  const [getLabel] = useLabels();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const [isExactDate, setIsExactDate] = useState(false);
  const axiosPrivate = useAxiosPrivate();
  const isFirstLoadRef = useRef(true);
  const [substancesWithQuantity, setSubstancesWithQuantity] = useState([]);
  const [filteredSubstances, setFilteredSubstances] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModifyMode, setIsModifyMode] = useState(-1);
  const [operationsP, setOperationsP] = useState([]);

  const [srcTankEnabledFieldArray, setSrcTankEnabledFieldArray] = useState([]);

  useEffect(() => {

    if (id && (!operations || operations?.length === 0)) return; // loadOperations();
    if (id && operations?.length > 0) {
      const operation = operations?.find((o) => o.id === Number(id)) || null;
      if (operation === null) return;

      setValue("priority", {
        label: getLabel(`operationPriority${operation?.priority}`),
        value: operation?.priority,
      });

      const srcs = [];
      operation?.src_tanks?.map((s) => {
        const tank = tanks?.find((t) => t.id === Number(s.tank_id));
        srcs.push({
          quantity: s?.quantity,
          tankData: {
            value: s?.tank_id,
            label: tank?.name,
            availability: Number(tank?.quantity),
            capacity: Number(tank?.capacity),
            quantity: Number(tank?.quantity),
            color: tank?.color,
            state: tank?.state,
            lot: tank?.batch_name || getLabel("tankState_EMPTY"),
            name: tank?.name + ` (${tank?.batch_name})`,
            batch_name: s?.batch_name,
          },
        });
      });
      srcTankFieldArray.replace(srcs);

      const cellarmans = [];
      operation?.cellarman_ids?.map((u) => {
        const user = users?.find((us) => us.id === u);
        cellarmans.push({
          value: u,
          label: user?.username,
        });
      });
      setValue("cellarman_ids", cellarmans);

      setValue("custom_name", operation?.custom_name || "");

      setValue("expire_date", new Date(operation?.expire_date));
      setValue("note", operation?.chat_notes?.[0]?.message || "");
      setIsExactDate(operation?.is_exact_date);
      
      const protocol = protocols?.find(
        (p) => p?.id === operation?.winemaking_protocol_id
      );
      setValue("winemaking_protocol_id", {
        value: operation?.winemaking_protocol_id,
        name: protocol?.name,
        protocol_type: protocol?.protocol_type,
      });

      // Populate the operation table from the saved treatments, exactly like
      // OperationsDetail/Details.js (AutomaticDetail) does.
      const aggregated = aggregateDrugsById(operation?.treatments || []) || [];
      setOperationsP(aggregated.map((a) => ({ ...a, opType: "TREATMENTS" })));
    }
  }, [id, operation, tanks, protocols]);

  const buildProtocolsData = () => {

    // console.log('substancesWithQuantityTmp', type, 'winemaking_protocol_id?.value', winemaking_protocol_id?.value, 'o', operation)
    if (!winemaking_protocol_id?.value) return; // Exit early if protocol_id is null
    if (!operation) return; // Exit if operation not found

    // Get substances
    const substances = operation?.treatments || [];

    const isUpdate =
      isFirstLoadRef.current &&
      id &&
      winemaking_protocol_id?.value === operation?.winemaking_protocol_id;
    
    const uniqueSubstanceIds = new Set(
      isUpdate ? [] : substances.map((item) => item.substance_id)
    );
    if (isUpdate) {
      operation?.treatments?.forEach((t) =>
        uniqueSubstanceIds.add(t?.substance_id)
      );
    }

    let substancesWithQuantityTmp = [];

    if (type === "TREATMENT" || type === "ADD") {

    // console.log('substancesWithQuantityTmp', 'substancesWithQuantityTmp')
      allSubstances
        ?.filter((s) => uniqueSubstanceIds.has(s.id))
        ?.forEach((treatment, i) => {
          const treatmentId = treatment.substance_id || treatment.id;
          if (
            substancesWithQuantityTmp?.some(
              (s) => s.substance_id === treatmentId
            )
          )
            return;

          const drug_batch_number = treatment.drug_batch_number || null;
          const additionalData =
            allSubstances?.find((s) => s.id === treatmentId) || {};
          const expire_date = drug_batch_number
            ? additionalData?.batches?.find(
                (b) => b.batch_number === drug_batch_number
              )?.expire_date || null
            : null;
          const protocolDrug = substances?.find(
            (s) => s.substance_id === treatmentId
          );

          substancesWithQuantityTmp.push({
            substance_id: treatmentId,
            substance_name: treatment.substance_name || treatment?.name,
            quantity: protocolDrug?.quantity ?? treatment.quantity ?? additionalData.quantity,
            min_range: treatment.min_range,
            max_range: treatment.max_range,
            unit: treatment.unit || additionalData?.unit,
            // unit_id is required by DrugBatchSelector to map the warehouse unit and
            // compute the required quantity; without it the batch "+" stays disabled.
            unit_id: additionalData?.unit_id ?? treatment?.unit_id,
            opType: "TREATMENT",
            removed: false,
            recommended_quantity: additionalData
              ? additionalData?.recommended_quantity
              : treatment?.recommended_quantity,
            batches: additionalData.batches,
            ...(drug_batch_number && {
              drug_batch_number,
              drug_batch_cost: treatment.drug_batch_cost,
            }),
            ...(expire_date && { expire_date }),
          });

          // Set drug batch data
          if (drug_batch_number) {
            setValue(`drug_batch.${i}`, {
              label: `${drug_batch_number} (${format(
                new Date(expire_date || treatment.expire_date),
                configs.shortDateFormat
              )})`,
              value: drug_batch_number,
            });
          }
        });
    }

    setSubstancesWithQuantity(getFilteredSubstances(substancesWithQuantityTmp));
  };

  const getFilteredSubstances = (substances = [], importDataState = null) => {
    if (["treatment", "add"].includes(type?.toLowerCase()))
      return substances?.map((s) => {
        return { ...s, opType: "PROTOCOL" };
      });
    else if (type?.toLowerCase() === "analysis") {
      const tmp = [];

      // Grouping function
      const grouped = substances
        ?.filter((s) =>
          importDataState !== null ? s?.states?.includes(importDataState) : true
        )
        ?.reduce((acc, item) => {
          const key = item.substance_name || item.name;
          if (!acc[key]) {
            acc[key] = [];
          }
          acc[key].push(item);

          return acc;
        }, {});
      // console.log('grouped', grouped)
      // Convert the result to an array of grouped objects (optional)
      Object.keys(grouped)?.forEach((key) => {
        const items = grouped[key];
        const firstItem = items?.[0];
        const substance_name = getLabel(`substance_${key}`);

        tmp.push({
          name: key,
          substance_id: firstItem?.substance_id,
          substance_name: key,
          substance_name_lower: substance_name?.toLowerCase(),
          unit: firstItem?.unit,
          unit_id: firstItem?.unit_id,
          // unit: firstItem?.unit,
          id: firstItem?.id,
          value: firstItem?.id,
          label: substance_name,
          items: items,
          states: firstItem?.states,
          opType: "PROTOCOL",
        });
      });

      return tmp?.sort((a, b) =>
        a?.substance_name_lower > b?.substance_name_lower
          ? 1
          : b?.substance_name_lower > a?.substance_name_lower
          ? -1
          : 0
      );
    } else return [];
  };

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    reset,
    setError,
    setValue,
    getValues,
    clearErrors,
    control,
  } = useForm({
    defaultValues: {
      // priority: operation?.priority || "",
      // protocolFrequency: "",
      // expire_date: operation?.expire_date || null,
      type: "ANALYSIS",
      src_tanks: operation?.src_tanks || [
        { tankData: { value: null, label: null } },
      ],
      cellarman_ids: operation?.cellarman_ids || "",
      custom_name: "",
    },
    mode: "onTouched",
  });

  const {
    register: modalRegister,
    handleSubmit: modalHandleSubmit,
    formState: { errors: modalErrors },
    setValue: modalSetValue,
    getValues: modalGetValues,
    setError: modalSetError,
    clearErrors: modalClearErrors,
    control: modalControl,
  } = useForm({
    defaultValues: {
      // day: protocol?.ay || "",
      // phase: protocol?.phase || "",
      // operation: protocol?.operation || "",
    },
    mode: "onTouched",
  });

  const srcTankFieldArray = useFieldArray({
    control,
    name: "src_tanks",
  });

  // Drug-batch field arrays required by SubstanceList (treatment-type rendering)
  const drugBatchFieldArray = useFieldArray({
    control,
    name: "drug_batches",
  });
  const drugBatchFieldArrayTmp = useFieldArray({
    control,
    name: "drug_batches_tmp",
  });

  const { winemaking_protocol_id, src_tanks } = useWatch({ control });
  const previousProtocolIdRef = useRef();

  // Total treated volume (used by SubstanceList to enable/size the drug-batch selector).
  // Prefer the operation's src-tank quantity (the volume being treated); fall back to the
  // tank's current quantity for new operations.
  const totalTreatmentVolume = (src_tanks || []).reduce(
    (acc, t) => acc + (Number(t?.quantity) || Number(t?.tankData?.quantity) || 0),
    0
  );

  useEffect(() => {
    if (type === "ADD") {
      buildProtocolsData();
      // Addable products for the SubstanceList "+" control
      setFilteredSubstances(getFilteredSubstances(allSubstances));
      return;
    }
    if (!winemaking_protocol_id?.value) return; // Exit if winemaking_protocol_id is null

    const protocol =
      protocols?.find((p) => p?.id === winemaking_protocol_id?.value) || null;

    if (!protocol) return; // Exit if protocol is not found

    const state = protocol?.state;
    const prevState = previousProtocolIdRef.current
      ? protocols?.find((p) => p?.id === previousProtocolIdRef.current)?.state
      : null;

    // Reset src_lots and src_tanks if the protocol state changes
    if (!isFirstLoadRef.current && state !== prevState) {
      srcTankFieldArray.replace([{ tankData: { value: null, label: null } }]);
    }

    // Update substances based on the new protocol
    setFilteredSubstances(getFilteredSubstances(allSubstances, state));
  
    // Update protocol-related substances
    if (winemaking_protocol_id?.value !== previousProtocolIdRef.current) {
      buildProtocolsData();
    }
    // Update reference for protocol_id
    previousProtocolIdRef.current = winemaking_protocol_id?.value;

    // Mark that the first load has completed so the editing of an existing op does not reset the fields
    isFirstLoadRef.current = false;

  }, [winemaking_protocol_id, protocols, allSubstances]); // re-run on protocol/substances change (so fresh drug batches are picked up)

  const columnHelper = createColumnHelper();
  // Columns mirror OperationsDetail/Details.js (AutomaticDetail) so the form
  // shows the same treatment data: product name, quantity, type, temperature
  // range and product batches (no day/phase/operation columns).
  const columns = ["HOMOGENIZATION_MASS", "OXYGENATION"].includes(type)
    ? [
        columnHelper.accessor(
          (row) => (row?.type ? getLabel(`protocolOperation_${row.type}`) : ""),
          {
            header: getLabel("protocolOperation"),
            cell: TableCell,
            meta: { type: "text", isFilterDisabled: true },
          }
        ),
        columnHelper.accessor(
          (row) =>
            row?.protocol_type
              ? ["MICRO", "MACRO", "CUSTOM"].includes(row?.protocol_type)
                ? getLabel(`protocolOXYGENATION_${row?.protocol_type}`)
                : ["HOMOGENIZATION_TEMP", "HOMOGENIZATION_REASSEMBLY"].includes(
                    row?.protocol_type
                  )
                ? getLabel(
                    `protocolType_HOMOGENIZATION_${row?.protocol_type.replace(
                      "HOMOGENIZATION_",
                      ""
                    )}`
                  ) +
                  (row?.protocol_type === "HOMOGENIZATION_REASSEMBLY" &&
                  row?.homogenization_type
                    ? ` (${getLabel(
                        `protocolHomogenizationMass_${row?.homogenization_type}`
                      )})`
                    : "")
                : ""
              : "",
          {
            header: getLabel("protocolType"),
            cell: TableCell,
            meta: { type: "text", isFilterDisabled: true },
          }
        ),
        ...(operationsP?.[0]?.protocol_type === "HOMOGENIZATION_REASSEMBLY"
          ? [
              columnHelper.accessor(
                (row) =>
                  row?.work_time &&
                  (row?.pause_time || row?.homogenization_pause_time)
                    ? `${row?.work_time} min/${
                        row?.pause_time || row?.homogenization_pause_time
                      }h ${
                        (type === "CIP" ||
                          row.operation === "HOMOGENIZATION_MASS") &&
                        row?.iter_per_day
                          ? `x ${row?.iter_per_day}`
                          : ""
                      }`
                    : "",
                {
                  header: getLabel("protocolWorkPauseTime"),
                  cell: TableCell,
                  meta: { type: "number", isFilterDisabled: true },
                }
              ),
              columnHelper.accessor(
                (row) => (row?.motor_speed ? row?.motor_speed + " %" : ""),
                {
                  header: getLabel("protocolMotorSpeed"),
                  cell: TableCell,
                  meta: { type: "number", isFilterDisabled: true },
                }
              ),
            ]
          : type === "OXYGENATION" ||
            operationsP?.[0]?.protocol_type === "HOMOGENIZATION_TEMP"
          ? [
              columnHelper.accessor(
                (row) =>
                  row?.quantity
                    ? row?.protocol_type === "HOMOGENIZATION_TEMP"
                      ? `${row?.quantity} \u00b0C`
                      : (row?.quantity || 0) +
                        " " +
                        (row?.type === "OXYGENATION" && row.protocol_type
                          ? row?.protocol_type === "CUSTOM"
                            ? `${getLabel("protocolOxygenationNoDose_CUSTOM")} ${
                                row?.oxygenation_dose_hours || ""
                              }h:${row?.oxygenation_dose_minutes || ""}m`
                            : getLabel(
                                `protocolOxygenationNoDose_${row.protocol_type}`
                              )
                          : "")
                    : "",
                {
                  header: getLabel(
                    type === "HOMOGENIZATION_MASS"
                      ? "sensorSetPoint"
                      : "protocolQuantity"
                  ),
                  cell: TableCell,
                  meta: { type: "number", isFilterDisabled: true },
                }
              ),
            ]
          : []),
        columnHelper.accessor(
          (row) => (row?.iter_per_day ? `x${row?.iter_per_day}` : "\u2014" || ""),
          {
            header: getLabel("protocolHomogenizationIterPerDay"),
            cell: TableCell,
            meta: { type: "text", isFilterDisabled: true },
          }
        ),
      ]
    : [
        columnHelper.accessor("substance_name", {
          header: getLabel("treatmentName"),
          cell: TableCell,
        }),
        columnHelper.accessor((row) => `${row?.quantity} ${row?.unit || ""}`, {
          header: getLabel("drugQuantity"),
          cell: TableCell,
          meta: { type: "number", isFilterDisabled: true },
        }),
        columnHelper.accessor(
          (row) =>
            row?.protocol_type
              ? getLabel(`drugLabelGroup_${row?.protocol_type}_One`)
              : "",
          {
            header: getLabel("drugType"),
            cell: TableCell,
            meta: { type: "text", isFilterDisabled: true, filterVariant: "text" },
          }
        ),
        columnHelper.accessor(
          (row) =>
            `${row?.min_temperature ? String(row?.min_temperature) : "n/a"} - ${
              row?.max_temperature ? String(row?.max_temperature) : "n/a"
            } \u00b0C` || "",
          {
            header: getLabel("substanceTemperature"),
            cell: TableCell,
            meta: { type: "number", isFilterDisabled: true },
          }
        ),
        columnHelper.accessor(
          (row) =>
            Array.isArray(row.drug_batches)
              ? [...new Set(row.drug_batches.map((b) => b.batch_number))].join(
                  ", "
                )
              : "",
          {
            header: getLabel("drugBatchNumbers"),
            cell: TableCell,
            meta: { isFilterDisabled: true },
          }
        ),
      ];

  const handleCloseModal = () => {
    setIsOpen(false);
  };

  const setModifyMode = (row_id) => {
    setIsModifyMode(row_id);
  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const openFormModal = () => {
    setIsOpen(true);
  };

  const customIdNewOp = "custom-id-new-op";
  const customIdUpdateOp = "custom-id-update-op";

  const submitForm = async (d) => {
    const data = d ? d : getValues();
    const src_tank_ids = data?.src_tanks?.map(
      (s) => s?.tankData?.id || s?.tankData?.value
    );

    if (new Date(data?.expire_date?.setHours(23, 59, 59)) < new Date()) {
      setError("expire_date", {
        type: "manual",
        message: getLabel("errorDateInThePast"),
      });
      return;
    }

    const src_tanks = src_tank_ids?.map((t) => {
      return { tank_id: t };
    });

    for (let i = 0; i < src_tank_ids?.length; i++) {
      const t = tanks?.find((tt) => tt?.id == src_tank_ids[i]);
      if (Number(t?.quantity) <= 0) {
        setError(`src_tanks.${i}.tankData`, {
          type: "manual",
          message: getLabel("error0QuantityNewModify"),
        });
        return;
      }
    }

    const requestPayload = {
      src_tanks: src_tanks,
      priority: data?.priority?.value,
      // ...(expire_dates?.length === 0 && { expire_date: addMinutes(
      //   new Date(data?.expire_date),
      //   -new Date(data?.expire_date).getTimezoneOffset()
      // ).toISOString() }),
      is_exact_date: isExactDate,
      expire_date: new Date(
        data?.expire_date?.setHours(23, 59, 59)
      ).toISOString(),
      type: type,
      winery_id: activeWinery?.id,
      winemaking_protocol_id: winemaking_protocol_id?.value,
      cellarman_ids: data?.cellarman_ids?.map((cellMan) => cellMan?.value),
      note: data?.note?.trim(),
      custom_name: data?.custom_name?.trim(),
    };

    const treatments_data = [];
    const tmp_treatments_data = [];

    const currentProtocol = protocols?.find(
      (s) => s.id === winemaking_protocol_id?.value
    );
    const removedSubIds = [];
    const addedSubIds = [];

    // initialize added/removed ids lists
    if (
      typeof data?.removed === "object" &&
      Object.keys(data?.removed || {}).length > 0
    ) {
      for (const [substance_id, isRemoved] of Object.entries(data?.removed)) {
        if (isRemoved?.value === true || isRemoved === true)
          removedSubIds.push(Number(substance_id));
        else if (isRemoved?.value === false || isRemoved === false)
          addedSubIds.push(Number(substance_id));
      }
    }
    const protocol = protocols?.find(
      (p) => p?.id === operation?.winemaking_protocol_id
    );

    if (!id) {
      // set default substances
      currentProtocol?.[["TREATMENT", "ADD"].includes(type) ? "drugs" : "analysis"]?.forEach(
        (s) => {
          const substance = allSubstances?.find(
            (ss) => ss?.id === Number(s?.substance_id)
          );
          const val = Number(s?.quantity);
          const tmp = {
            substance_id: substance?.id,
            quantity: val,
            unit_id: substance?.unit_id,
          };
          tmp.removed = removedSubIds?.includes(substance?.id);
          if (tmp.removed === false) tmp.added = true;

          const foundIndex = tmp_treatments_data?.findIndex(
            (t) => t?.substance_id === substance?.id
          );

          if (foundIndex > -1) tmp_treatments_data[foundIndex] = tmp;
          else tmp_treatments_data.push(tmp);
        }
      );
    } else {
      const operation = operations?.find((o) => o.id === Number(id)) || null;
      operation?.treatments?.forEach((t) => {
        if (
          t.substance_id !== undefined &&
          !addedSubIds.includes(t.substance_id)
        ) {
          addedSubIds.push(t.substance_id);
        }
      });

      operation?.treatments?.forEach((t) => {
        const tmp = { ...t };
        tmp.removed = removedSubIds?.includes(t?.substance_id);
        if (
          tmp_treatments_data?.find(
            (tt) => tt?.substance_id === t?.substance_id
          ) === undefined
        ) {
          // remove the 'tank_id' and 'batch_id' properties
          delete tmp.tank_id;
          delete tmp.batch_id;
          tmp_treatments_data.push(tmp);
        }
      });
    }
    if (
      typeof data?.column === "object" &&
      Object.keys(data?.column || {}).length > 0
    ) {
      for (const [substance_id, new_value] of Object.entries(data?.column)) {
        // if (!new_value?.id && !new_value?.substance_id) continue;
        if (!new_value?.substance_id) continue;

        const substance = allSubstances?.find(
          (s) => s?.id === Number(new_value?.id || substance_id)
        );
        if (substance !== null) {
          const substance_id = substance?.substance_id || substance?.id;
          const foundIndex = tmp_treatments_data?.findIndex(
            (t) => t?.substance_id === substance_id
          );
          const val =
            Number(new_value?.new) >= 0
              ? Number(new_value?.new)
              : substance?.quantity || 0;

          const tmp = {
            ...(foundIndex > -1 && { ...tmp_treatments_data[foundIndex] }),
            substance_id: substance_id,
            quantity: val,
            unit_id: substance?.unit_id,
            unit: substance?.unit,
            max_range: substance?.max_range,
            min_range: substance?.min_range,
            winemaking_protocol_id: data?.winemaking_protocol_id?.value,
            substance_name: substance?.name,
            ...(addedSubIds?.includes(substance?.id) && { added: true }),
            ...(removedSubIds?.includes(substance?.id) && { removed: true }),
          };

          if (foundIndex > -1) {
            tmp_treatments_data[foundIndex] = tmp;
          } else if (
            tmp?.substance_id &&
            (type === "ANALYSIS" || (["TREATMENT", "ADD"].includes(type) && val > 0))
          ) {
            tmp_treatments_data.push(tmp);
          }
        }
      }
    }

    if (["TREATMENT", "ADD"].includes(type)) {
      // drug batches set
      if (
        typeof data?.drug_batch === "object" &&
        Object.keys(data?.drug_batch || {}).length > 0
      ) {
        for (const treatment of tmp_treatments_data) {
          const drug_batch =
            data?.drug_batch?.find(
              (b) => b?.substance_id === treatment?.substance_id
            ) || null;
          if (
            addedSubIds?.includes(treatment?.substance_id) ||
            !protocol?.substance_ids?.includes(treatment?.substance_id)
          )
            treatment.added = true;

          if (drug_batch === null) continue;
          const drug_batch_cost = allSubstances
            ?.flatMap((item) => item.batches)
            ?.find((batch) => batch.batch_number === drug_batch?.value)?.cost;
          treatment.drug_batch_cost = drug_batch_cost;
          treatment.drug_batch_number = drug_batch?.value;
        }
      }
      treatments_data.push(...tmp_treatments_data);
    }
    requestPayload.treatments_data = treatments_data;
    // console.log("treatments_data", treatments_data);
    // return;

    console.log("requestPayload", requestPayload);
    // return;
    let response = null;

    if (id && !isDuplicate) {
      response = await toastAxios(
        () => updateOperation(id, requestPayload, axiosPrivate),
        customIdUpdateOp,
        { name: operation?.type || "" },
        getLabel,
      );
    } else {
      response = await toastAxios(
        () => newOperation(requestPayload, axiosPrivate),
        customIdNewOp,
        { name: operation?.type || "" },
        getLabel,
      );
    }
    console.log("response", response);

    if (response && response?.success) {
      const updated_op = response?.updated_data || null;
      updateStore(updated_op);
      // dispatch(empty());
      navClickHandler();
    } else {
      handleCloseModal();
    }
  };

  const updateStore = (payload) => {
    if (id && !isDuplicate) {
      dispatch(updateOp(payload));
    } else {
      dispatch(add(payload));
    }
  };

  const navClickHandler = () => {
    if (id) {
      navigate(`/programming/${type?.toLowerCase?.()}/${id}`);
    } else {
      navigate("/programming");
    }
  };

  return (
    <div className="operation_form_wrapper">
      <form onSubmit={handleSubmit(submitForm, (errors) => console.log("Form validation errors:", errors))} autoComplete="off" noValidate>
        <h4>{getLabel("treatmentsConfiguration")}</h4>
        <div>
          <div className="operation_form_wrapper_row vertical">
            <Label label={getLabel(winemaking_protocol_id?.protocol_type === "PRE" ? "wineMakingProtocols" : "wineMakingPostProtocols")}>
              {winemaking_protocol_id?.name || ""}
            </Label>
          </div>
          <div className="operation_form_wrapper_row">
            {winemaking_protocol_id?.value && (
              <div className="operation_form_wrapper_row vertical full_width">
                {type === "ADD" ? (
                  // ADD: use SubstanceList so products can be added/removed (the "+")
                  <SubstanceList
                    isProtocolApplication={true}
                    register={register}
                    errors={errors}
                    control={control}
                    substanceType={"substance"}
                    substances={substancesWithQuantity}
                    allSubstances={filteredSubstances}
                    isSearchBar={false}
                    reset={reset}
                    clearErrors={clearErrors}
                    setValue={setValue}
                    getValues={getValues}
                    isCheckbox={true}
                    disabledIds={operation?.treatments || []}
                    automaticDrugBatches={drugBatchFieldArray}
                    drugBatches={drugBatchFieldArrayTmp}
                    totalTreatmentVolume={totalTreatmentVolume}
                    operation_id={operation?.id}
                  />
                ) : (
                  <TableEdit
                    defaultData={operationsP}
                    columns={columns}
                    // errors={errors}
                    errors={modalErrors}
                    control={modalControl}
                    isEditable={type === "CLEANING"}
                    isLegend={false}
                    isSearchBar={false}
                    // setValue={setValue}
                    setValue={modalSetValue}
                    getValues={modalGetValues}
                    type={"winemaking"}
                    modalHandler={handleOpenModal}
                    setModifyMode={setModifyMode}
                    setRows={setOperationsP}
                  />
                )}
              </div>
            )}
          </div>
        </div>
        <h4>{getLabel("operationTanksConfiguration")}</h4>
        <div className="operation_form_wrapper_row vertical">
          {srcTankFieldArray?.fields?.map?.((field, idx) => {
            return (
              <div key={field.id} className="operation_form_wrapper_row">
                <Select
                  label={getLabel("operationSrcTankName")}
                  placeholder={getLabel("operationSrcTanksPlaceholder")}
                  error={errors}
                  required={getLabel("inputRequiredError")}
                  name={`src_tanks.${idx}.tankData`}
                  isSearchable={true}
                  control={control}
                  options={tanks
                    ?.filter(
                      (tank) =>
                        tank?.quantity > 0 &&
                        !srcTankFieldArray?.fields?.some(
                          (field) => field?.tankData?.value == tank?.id
                        )
                    )
                    ?.sort((a, b) => {
                      // case insensitive
                      return (a.name || "")?.localeCompare(
                        b.name || "",
                        undefined,
                        {
                          numeric: true,
                          sensitivity: "base", // ignore upper/lowercase and accents
                        }
                      );
                    })
                    ?.map((tank) => {
                      return {
                        ...tank,
                        value: tank?.id,
                        label: tank.name,
                        name:
                          tank?.name +
                          ` (${
                            lots?.find((lot) => lot?.id === tank?.batch_id)
                              ?.name || getLabel("tankState_EMPTY")
                          })`,
                        quantity: tank.quantity,
                        capacity: tank.capacity,
                        batch_type:
                          tank?.batch_type || tank?.planned_batch_type,
                        color: tank.color,
                        lot: tank.batch_name,
                        lot_id: tank?.batch_id || "",
                      };
                    })}
                  tooltip={
                    idx === 0 ? getLabel("analysisSourceTankTooltip") : ""
                  }
                  // onTableChange={() => checkState(idx, src_tanks?.[idx]?.tankData?.value)}
                />
                {src_tanks?.[idx]?.tankData?.value && (
                  <TankIcon
                    tank={{
                      ...src_tanks?.[idx]?.tankData,
                      name: src_tanks?.[idx]?.tankData?.label,
                    }}
                    isArrowQuantity={false}
                  />
                )}
                {srcTankFieldArray?.fields?.length > 1 && (
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      srcTankFieldArray?.remove(idx);
                    }}
                  >
                    -
                  </button>
                )}
              </div>
            );
          })}
          <button
            onClick={(e) => {
              e.preventDefault();
              srcTankFieldArray?.append({
                tankData: {},
              });
            }}
          >
            +
          </button>
        </div>
        <div className="operation_form_wrapper_row">
          <MultipleSelect
            name={`cellarman_ids`}
            control={control}
            label={getLabel("operationUser")}
            placeholder={getLabel("operationUserPlaceholder")}
            error={errors["cellarman_ids"]}
            required={getLabel("inputRequiredError")}
            options={users?.map?.((user) => {
              return {
                value: user?.id,
                label: user?.username,
              };
            })}
            tooltip={getLabel("destemmingCellarmenTooltip")}
          />
          <Select
            name="priority"
            control={control}
            label={getLabel("operationPriority")}
            placeholder={getLabel("operationPriorityPlaceholder")}
            error={errors}
            required={getLabel("inputRequiredError")}
            options={[
              {
                label: getLabel("lotQuality_HIGH"),
                value: "2",
              },
              {
                label: getLabel("lotQuality_MEDIUM"),
                value: "1",
              },
              {
                label: getLabel("lotQuality_LOW"),
                value: "0",
              },
            ]}
            // defaultValue={{
            //     value: "",
            //     label: "",
            //   }}
          />
          <DatePicker
            name="expire_date"
            control={control}
            label={getLabel("operationExpireDateForm")}
            error={errors["expire_date"]}
            required={getLabel("inputRequiredError")}
            dateFormat={configs.shortDateFormat}
            placeholder={getLabel("expiringDatePlaceholder")}
          />
          {getValues("expire_date") && (
            <Input
              name={"expire_date_exact"}
              register={register}
              label={getLabel("isExactDate")}
              error={errors["expire_date_exact"]}
              // isLabel={false}
              type={"checkbox"}
              checked={isExactDate}
              onChange={() => setIsExactDate((prev) => !prev)}
              customClassName={"confirm no_margin_top"}
              tooltip={getLabel("destemmingExactDate")}
            />
          )}
        </div>
        <div className="client_form_wrapper_row">
          <TextArea
            name="note"
            register={register}
            label={getLabel("clientNotes")}
            placeholder={getLabel("clientNotesPlaceholder")}
            error={errors["note"]}
          />
        </div>
        <div className="operation_form_wrapper_row">
          <Submit label={getLabel("submitForm")}></Submit>
        </div>
      </form>
      <ConfirmationModal
        isOpen={isOpen}
        onConfirm={submitForm}
        onClose={handleCloseModal}
        description={getLabel("modalNewRepetitiveAnalysis")}
        isOperationRecap={true}
        totalStep={0}
        errors={errors}
        getValues={getValues}
        // configs={configs}
      >
        <OperationRecap
          type={"RECURRENT_ANALYSIS"}
          control={control}
          register={register}
          getValues={getValues}
          errors={errors}
          configs={configs}
        />
      </ConfirmationModal>
    </div>
  );
};
export default AutomaticForm;
